import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { existingPassExtensionDays, isPassEligibleOn, passUsableThroughDate, schoolDate } from "../../worker/src/domain";
import { api, post, remove } from "../api";
import { useI18n } from "../i18n";
import { dateInTimeZone } from "../time";
import type { AdminResponse, Attendance, CorrectionsResponse, Pass, Payment, Session } from "../types";
import { ActionMenu, ActionMenuDivider, ActionMenuItem } from "./ActionMenu";
import { AttendanceMeta, effectiveChargeType } from "./Attendance";

export type MutationRunner = (action: () => Promise<unknown>, success?: string) => Promise<unknown | undefined>;

const requestId = () => crypto.randomUUID();
function reason(promptText: string, preset: string) {
  const value = window.prompt(promptText + " (optional)", preset);
  return value === null ? null : value.trim();
}

export function SessionActions({ session, data, working, mutate, onEdit, onToggleStatus, assignmentControl }: {
  session: Session; data: AdminResponse; working: boolean; mutate: MutationRunner;
  onEdit?: (session: Session) => void;
  onToggleStatus?: (session: Session) => void;
  assignmentControl?: ReactNode;
}) {
  const removalRequestId = useRef(crypto.randomUUID());
  if (data.staff.role !== "owner") return null;


  async function cancel() {
    const why = reason("Reason for cancelling this class", "Class cancelled");
    if (why === null || !window.confirm("This class will stop accepting QR and manual check-ins. Existing attendance will remain.")) return;
    await mutate(() => post(`/api/admin/sessions/${session.session_id}/cancel`, { requestId: requestId(), reason: why }), "Class cancelled");
  }

  async function deleteUnused() {
    const why = reason("Reason for permanently deleting this unused class", "Unused class deleted");
    if (why === null || !window.confirm("Permanently delete this class? This is allowed only when it has no related records.")) return;
    await mutate(() => remove(`/api/admin/sessions/${session.session_id}`, { requestId: requestId(), reason: why }), "Unused class deleted");
  }

  async function removeOccurrence() {
    if (!window.confirm("Remove only this class date? The regular timetable will keep a cancellation marker so it is never generated again.")) return;
    await mutate(() => post(`/api/admin/sessions/${session.session_id}/remove-occurrence`, {
      requestId: removalRequestId.current, reason: "Single regular occurrence removed",
    }), "This class was removed");
  }

  const generated = Boolean(session.class_id);
  const hasOperationalHistory = Number(session.operational_record_count || 0) > 0;

  return <ActionMenu>
      <ActionMenuItem disabled={working} onClick={() => onEdit?.(session)}>Edit</ActionMenuItem>
      {onToggleStatus && session.status !== "cancelled" && <ActionMenuItem disabled={working} onClick={() => onToggleStatus(session)}>
        {session.status === "open" ? "Close class" : "Open class"}
      </ActionMenuItem>}
      {assignmentControl}
      {(session.status !== "cancelled" || (!generated && !Number(session.attendance_count || 0) && !Number(session.payment_count || 0)))
        && <ActionMenuDivider />}
      {session.status !== "cancelled" && generated && !hasOperationalHistory
        && <ActionMenuItem className="danger-action" disabled={working} onClick={() => void removeOccurrence()}>Remove this class</ActionMenuItem>}
      {session.status !== "cancelled" && (!generated || hasOperationalHistory)
        && <ActionMenuItem className="danger-action" disabled={working} onClick={() => void cancel()}>Cancel class</ActionMenuItem>}
      {!generated && !Number(session.attendance_count || 0) && !Number(session.payment_count || 0)
        && <ActionMenuItem className="danger-action" disabled={working} onClick={() => void deleteUnused()}>Delete unused class</ActionMenuItem>}
  </ActionMenu>;
}

export function AttendanceActions({ row, data, working, mutate }: {
  row: Attendance; data: AdminResponse; working: boolean; mutate: MutationRunner;
}) {
  const targetSessions = data.sessions.filter((session) =>
    session.session_id !== row.session_id && session.status !== "cancelled");
  const validPasses = data.passes.filter((pass) =>
    pass.student_id === row.student_id && isPassEligibleOn(pass, row.class_date));
  const [targetSessionId, setTargetSessionId] = useState(targetSessions[0]?.session_id || "");
  const [targetPassId, setTargetPassId] = useState(validPasses[0]?.pass_id || "");
  if (data.staff.role !== "owner") return null;
  async function voidRecord() {
    const why = reason("Reason for voiding this check-in", "Attendance correction");
    if (why === null) return;
    const effectiveCharge = effectiveChargeType(row.charge_type, row.billing_intent);
    const consequence = row.charge_type === "pass"
      ? "This will void the check-in and restore exactly 1 pass credit."
      : effectiveCharge === "pass_pending"
        ? "This will void the check-in. No payment or pass will be changed."
        : row.charge_type === "dropin"
          ? "This will void the check-in and cancel its unpaid drop-in payment."
          : "This will void the check-in. The unlimited pass balance will not change.";
    if (!window.confirm(consequence)) return;
    await mutate(() => post(`/api/admin/attendance/${row.attendance_id}/void`, { requestId: requestId(), reason: why }), "Check-in voided");
  }
  async function move() {
    if (!targetSessionId) { window.alert("Choose a target class"); return; }
    const why = reason("Reason for moving this check-in", "Moved to correct class");
    if (why === null || !window.confirm("This atomically voids the original check-in, reverses its charge, and creates a newly charged attendance in the selected class.")) return;
    await mutate(() => post(`/api/admin/attendance/${row.attendance_id}/move`, {
      requestId: requestId(), reason: why, targetSessionId,
    }), "Check-in moved");
  }
  async function correctCharge(target: "pass" | "dropin") {
    if (target === "pass" && !targetPassId) { window.alert("This student has no valid pass for the class date"); return; }
    const why = reason(`Reason for changing this charge to ${target === "pass" ? "a pass" : "drop-in"}`, target === "pass" ? "Use pass instead" : "Use drop-in instead");
    if (why === null) return;
    const consequence = target === "pass"
      ? row.billing_intent === "pass_pending"
        ? "This will deduct one valid pass credit and resolve the pending pass purchase."
        : "This will deduct one valid pass credit and void the drop-in payment."
      : "This will restore the original pass credit and create one unpaid drop-in payment.";
    if (!window.confirm(consequence)) return;
    await mutate(() => post(`/api/admin/attendance/${row.attendance_id}/correct-charge`, {
      requestId: requestId(), reason: why, target, passId: target === "pass" ? targetPassId : null,
    }), "Charge corrected");
  }
  return <ActionMenu>
      <label className="action-menu-field"><span>Target class</span><select value={targetSessionId} onChange={(event) => setTargetSessionId(event.target.value)}>
        <option value="">Choose class</option>
        {targetSessions.map((session) => <option key={session.session_id} value={session.session_id}>
          {session.class_date} {session.start_time} {session.title}
        </option>)}
      </select></label>
      <ActionMenuItem disabled={working || !targetSessionId} onClick={() => void move()}>Move to another class</ActionMenuItem>
      {effectiveChargeType(row.charge_type, row.billing_intent) !== "pass" && row.charge_type === "dropin" && <label className="action-menu-field"><span>Pass to use</span><select value={targetPassId} onChange={(event) => setTargetPassId(event.target.value)}>
        <option value="">Choose pass</option>
        {validPasses.map((pass) => <option key={pass.pass_id} value={pass.pass_id}>
          {pass.pass_type === "unlimited" ? "Unlimited" : String(pass.remaining_classes) + " classes"} / {pass.expiry_date || "no expiry"}
        </option>)}
      </select></label>}
      {row.charge_type === "dropin"
        ? <ActionMenuItem disabled={working} onClick={() => void correctCharge("pass")}>Use pass instead</ActionMenuItem>
        : <ActionMenuItem disabled={working} onClick={() => void correctCharge("dropin")}>Use drop-in instead</ActionMenuItem>}
      <ActionMenuDivider />
      <ActionMenuItem className="danger-action" disabled={working} onClick={() => void voidRecord()}>Void check-in</ActionMenuItem>
  </ActionMenu>;
}

export function HistoricalAttendanceModal({ data, fixedStudentId, fixedSessionId, passes = data.passes,
  working, mutate, onCancel, onSaved }: {
  data: AdminResponse; fixedStudentId?: string; fixedSessionId?: string; passes?: Pass[];
  working: boolean; mutate: MutationRunner; onCancel: () => void; onSaved?: () => void | Promise<void>;
}) {
  const { language } = useI18n();
  const students = data.students.filter((student) => student.status !== "merged");
  const sessions = [...data.sessions].filter((session) => session.status !== "cancelled")
    .sort((left, right) => right.class_date.localeCompare(left.class_date)
      || right.start_time.localeCompare(left.start_time));
  const [studentId, setStudentId] = useState(fixedStudentId || students[0]?.student_id || "");
  const [sessionId, setSessionId] = useState(fixedSessionId || sessions[0]?.session_id || "");
  const [billingMethod, setBillingMethod] = useState<"pass" | "dropin" | "pass_pending">("pass");
  const [passId, setPassId] = useState("");
  const [requestId] = useState(() => crypto.randomUUID());
  const session = sessions.find((item) => item.session_id === sessionId);
  const student = students.find((item) => item.student_id === studentId);
  const validPasses = passes.filter((pass) => pass.student_id === studentId && session
    && isPassEligibleOn(pass, session.class_date)
    && (!pass.activated_at || dateInTimeZone(data.settings.timeZone, new Date(pass.activated_at)) <= session.class_date));
  const duplicate = data.attendance.some((row) => row.student_id === studentId
    && row.session_id === sessionId && !row.voided_at);
  useEffect(() => {
    if (!validPasses.some((pass) => pass.pass_id === passId)) setPassId(validPasses[0]?.pass_id || "");
  }, [passId, validPasses]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!student || !session || duplicate || (billingMethod === "pass" && !passId)) return;
    const value = new FormData(event.currentTarget);
    const result = await mutate(() => post("/api/admin/manual-checkins", {
      requestId, studentId, sessionId, billingMethod,
      passId: billingMethod === "pass" ? passId : null,
      reason: String(value.get("reason") || "").trim(),
    }), language === "zh" ? "上課紀錄已補回" : "Historical attendance added");
    if (result) { await onSaved?.(); onCancel(); }
  }
  const billingLabel = billingMethod === "pass" ? (language === "zh" ? "使用課卡" : "Use Pass")
    : billingMethod === "dropin" ? "Drop-in" : "Pass pending";
  return <div className="modal-backdrop" role="presentation"><section className="confirmation-modal manual-correction-modal"
    role="dialog" aria-modal="true" aria-labelledby="manual-correction-title">
    <h2 id="manual-correction-title">{language === "zh" ? "補回紀錄" : "Manual correction"}</h2>
    <form onSubmit={submit}>
      {fixedStudentId ? <p><strong>{language === "zh" ? "學生" : "Student"}:</strong> {student?.display_name}</p>
        : <label><span>{language === "zh" ? "學生" : "Student"}</span><select value={studentId} onChange={(event) => setStudentId(event.target.value)} required>
          {students.map((item) => <option key={item.student_id} value={item.student_id}>{item.display_name}</option>)}</select></label>}
      {fixedSessionId ? <p><strong>{language === "zh" ? "課堂" : "Class"}:</strong> {session?.class_date} {session?.start_time} {session?.title}</p>
        : <label><span>{language === "zh" ? "課堂" : "Class"}</span><select value={sessionId} onChange={(event) => setSessionId(event.target.value)} required>
          {sessions.map((item) => <option key={item.session_id} value={item.session_id}>{item.class_date} {item.start_time} {item.title}</option>)}</select></label>}
      <label><span>{language === "zh" ? "收費方式" : "Billing method"}</span><select value={billingMethod}
        onChange={(event) => setBillingMethod(event.target.value as typeof billingMethod)}>
        <option value="pass">{language === "zh" ? "使用課卡" : "Use Pass"}</option><option value="dropin">Drop-in</option><option value="pass_pending">Pass pending</option>
      </select></label>
      {billingMethod === "pass" && <label><span>{language === "zh" ? "課卡" : "Pass"}</span><select value={passId}
        onChange={(event) => setPassId(event.target.value)} required><option value="">{language === "zh" ? "選擇適用課卡" : "Choose eligible pass"}</option>
        {validPasses.map((pass) => <option key={pass.pass_id} value={pass.pass_id}>{pass.pass_type === "unlimited" ? "Unlimited" : `${pass.remaining_classes} classes`} · {pass.activated_at ? pass.expiry_date : (language === "zh" ? "將由此課堂啟用" : "activate from this class")}</option>)}</select></label>}
      <dl className="confirmation-summary"><div><dt>{language === "zh" ? "目前狀態" : "Current state"}</dt><dd>{duplicate ? (language === "zh" ? "已有有效點名" : "Attendance already exists") : (language === "zh" ? "沒有上課紀錄" : "No attendance")}</dd></div>
        <div><dt>{language === "zh" ? "修改後" : "New state"}</dt><dd>{student?.display_name || "—"} · {session ? `${session.class_date} ${session.start_time}` : "—"} · {billingLabel}</dd></div></dl>
      {billingMethod === "pass" && !validPasses.length && <p role="alert" className="form-error">{language === "zh" ? "此學生在該課堂日期沒有可用課卡。" : "This student has no eligible pass for that class date."}</p>}
      {duplicate && <p role="status" className="form-error">{language === "zh" ? "學生已有此課堂的有效上課紀錄。" : "The student already has attendance for this class."}</p>}
      <label><span>{language === "zh" ? "原因（可留空）" : "Reason (optional)"}</span><input name="reason" defaultValue={language === "zh" ? "補回上課紀錄" : "Historical attendance correction"} /></label>
      <div className="row-actions"><button className="primary-button" disabled={working || duplicate || !student || !session || (billingMethod === "pass" && !passId)}>{language === "zh" ? "儲存修正" : "Save correction"}</button>
        <button type="button" className="secondary-button" disabled={working} onClick={onCancel}>{language === "zh" ? "取消" : "Cancel"}</button></div>
    </form>
  </section></div>;
}

export function PassActivationModal({ pass, studentName, attendance, timeZone, working, mutate, onCancel, onSaved }: {
  pass: Pass; studentName: string; attendance: Attendance[]; timeZone: string; working: boolean;
  mutate: MutationRunner; onCancel: () => void; onSaved?: () => void | Promise<void>;
}) {
  const { language } = useI18n();
  const currentDate = pass.activated_at ? dateInTimeZone(timeZone, new Date(pass.activated_at)) : null;
  const earliestAttendanceDate = pass.earliest_attendance_date || attendance
    .filter((row) => row.pass_id === pass.pass_id && !row.voided_at)
    .map((row) => row.class_date).sort()[0] || null;
  const extensionDays = existingPassExtensionDays(pass.activated_at, pass.expiry_date, pass.validity_days, timeZone);
  const [activationDate, setActivationDate] = useState(currentDate || schoolDate(timeZone));
  const [requestId] = useState(() => crypto.randomUUID());
  const newExpiry = passUsableThroughDate(activationDate, pass.validity_days, extensionDays);
  const invalidAfterUse = Boolean(earliestAttendanceDate && activationDate > earliestAttendanceDate);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (invalidAfterUse) return;
    const value = new FormData(event.currentTarget);
    const result = await mutate(() => post(`/api/admin/passes/${encodeURIComponent(pass.pass_id)}/activation`, {
      requestId, activationDate, reason: String(value.get("reason") || "").trim(),
      ...(currentDate ? { pin: value.get("pin") } : {}),
    }), currentDate
      ? (language === "zh" ? "啟用日期已修改" : "Activation date changed")
      : (language === "zh" ? "課卡已啟用" : "Pass activated"));
    if (result) { await onSaved?.(); onCancel(); }
  }
  return <div className="modal-backdrop" role="presentation"><section className="confirmation-modal"
    role="dialog" aria-modal="true" aria-labelledby="pass-activation-title">
    <h2 id="pass-activation-title">{currentDate
      ? (language === "zh" ? "修改啟用日期" : "Change activation date")
      : (language === "zh" ? "啟用課卡" : "Activate pass")}</h2>
    <p><strong>{studentName}</strong></p><form onSubmit={submit}>
      <label><span>{language === "zh" ? "啟用日期" : "Activation date"}</span><input type="date" value={activationDate}
        onChange={(event) => setActivationDate(event.target.value)} required /></label>
      {earliestAttendanceDate && <button type="button" className="text-button" onClick={() => setActivationDate(earliestAttendanceDate)}>
        {language === "zh" ? "設為首次使用日期" : "Set to earliest pass attendance"} ({earliestAttendanceDate})</button>}
      <dl className="confirmation-summary"><div><dt>{language === "zh" ? "目前啟用日期" : "Current activation date"}</dt><dd>{currentDate || (language === "zh" ? "尚未啟用" : "Not activated")}</dd></div>
        <div><dt>{language === "zh" ? "新啟用日期" : "New activation date"}</dt><dd>{activationDate}</dd></div>
        <div><dt>{language === "zh" ? "目前最後可用日" : "Current usable-through date"}</dt><dd>{pass.expiry_date || "—"}</dd></div>
        <div><dt>{language === "zh" ? "新最後可用日" : "New usable-through date"}</dt><dd>{newExpiry}{extensionDays ? ` (+${extensionDays} days preserved)` : ""}</dd></div></dl>
      {invalidAfterUse && <p role="alert" className="form-error">{language === "zh"
        ? `此課卡已於 ${earliestAttendanceDate} 使用，啟用日期不能晚於首次使用日期。`
        : `This pass was already used on ${earliestAttendanceDate}. Activation cannot be later than its first use.`}</p>}
      <label><span>{language === "zh" ? "原因（可留空）" : "Reason (optional)"}</span><input name="reason"
        defaultValue={currentDate ? (language === "zh" ? "修改課卡啟用日期" : "Change pass activation date") : (language === "zh" ? "手動啟用課卡" : "Manual pass activation")} /></label>
      {currentDate && <label><span>{language === "zh" ? "確認 PIN" : "Confirmation PIN"}</span><input name="pin" type="password"
        inputMode="numeric" pattern="[0-9]{4}" minLength={4} maxLength={4} autoComplete="off" required /></label>}
      <div className="row-actions"><button className="primary-button" disabled={working || invalidAfterUse}>{language === "zh" ? "儲存修正" : "Save correction"}</button>
        <button type="button" className="secondary-button" disabled={working} onClick={onCancel}>{language === "zh" ? "取消" : "Cancel"}</button></div>
    </form></section></div>;
}

export function PaymentActions({ payment, data, working, mutate }: {
  payment: Payment; data: AdminResponse; working: boolean; mutate: MutationRunner;
}) {
  if (data.staff.role !== "owner") return null;
  async function correct(status: "unpaid" | "paid" | "waived" | "voided") {
    const why = reason(`Reason for changing this payment to ${status}`, `Payment marked ${status}`);
    if (why === null) return;
    let amountMinor: number | undefined;
    let method: string | undefined;
    let paymentDate: string | undefined;
    let note: string | undefined;
    if (status === "paid") {
      const amount = window.prompt("Amount (" + payment.currency + ")", String(payment.amount_minor / 100));
      if (amount === null) return;
      const parsedAmount = Number(amount);
      if (!Number.isFinite(parsedAmount) || parsedAmount < 0) { window.alert("Enter a valid non-negative amount"); return; }
      amountMinor = Math.round(parsedAmount * 100);
      method = window.prompt("Payment method", payment.method || "card")?.trim() || undefined;
      paymentDate = window.prompt("Payment date/time (ISO or YYYY-MM-DD)", new Date().toISOString())?.trim() || undefined;
      note = window.prompt("Optional payment note", "")?.trim() || undefined;
    }
    if (!window.confirm(`Payment will be marked ${status}. No record will be hard deleted.`)) return;
    await mutate(() => post(`/api/admin/payments/${payment.payment_id}/correct`, {
      requestId: requestId(), reason: why, status, amountMinor, method, paymentDate, note,
    }), `Payment marked ${status}`);
  }
  return <ActionMenu>
      {payment.status !== "paid" && <ActionMenuItem disabled={working} onClick={() => void correct("paid")}>Mark paid</ActionMenuItem>}
      {payment.status !== "waived" && <ActionMenuItem disabled={working} onClick={() => void correct("waived")}>Waive</ActionMenuItem>}
      {payment.status !== "unpaid" && <ActionMenuItem disabled={working} onClick={() => void correct("unpaid")}>Reopen</ActionMenuItem>}
      {payment.status !== "voided" && <><ActionMenuDivider />
        <ActionMenuItem className="danger-action" disabled={working} onClick={() => void correct("voided")}>Void</ActionMenuItem></>}
  </ActionMenu>;
}

export function CorrectionsPanel() {
  const [data, setData] = useState<CorrectionsResponse | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { api<CorrectionsResponse>("/api/admin/corrections").then(setData).catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load corrections")); }, []);
  if (error) return <p className="notice error">{error}</p>;
  if (!data) return <p className="muted">Loading correction history…</p>;
  return <div className="admin-stack">
    <section className="admin-panel"><h2>Voided check-ins</h2><div className="data-list">
      {data.voidedAttendance.map((row) => <article className="data-row" key={row.attendance_id}>
        <div><strong>{row.display_name}</strong><span>{row.title}</span><AttendanceMeta dateTime={row.checked_in_at} chargeType={row.charge_type} billingIntent={row.billing_intent} />
          <small>Voided: {row.void_reason}</small></div><span className="status-badge status-voided">Voided</span>
      </article>)}
    </div></section>
    <section className="admin-panel"><h2>Correction audit</h2><div className="data-list">
      {data.corrections.map((item) => <article className="data-row" key={String(item.request_id)}><div>
        <strong>{String(item.correction_type)}</strong><span>{String(item.entity_id)}</span>
        <small>{String(item.actor_name)} {String(item.created_at)} {String(item.reason)}</small>
      </div><span className="status-badge">{String(item.status)}</span></article>)}
    </div></section>
    <section className="admin-panel"><h2>Pass ledger</h2><div className="data-list">
      {data.passTransactions.map((item) => <article className="data-row" key={String(item.pass_transaction_id)}><div>
        <strong>{String(item.transaction_type)}</strong>
        <span>{item.class_title ? String(item.class_title) : String(item.pass_id)}</span>
        <small>{item.class_date
          ? `${String(item.class_date)} ${String(item.start_time || "")}`.trim()
          : String(item.created_at)}</small>
      </div><span className="status-badge">{Number(item.delta) > 0 ? "+" : ""}{String(item.delta)}</span></article>)}
    </div></section>
  </div>;
}
